import { useState } from 'react';
import styled from 'styled-components';
import { breakpoint, sizes } from 'variables';
import Loader from 'components/Loader/Loader';
import { format } from 'modules/helpers/strings';
import { getSortOptions } from 'components/reviews/helpers/sortHelper';
import Checkbox from 'components/shared/inputs/checkbox';
import { Spacing8, Spacing16, FontSize14 } from 'autogen/design-tokens/tokens';
import Select from 'components/shared/inputs/select';
import { ButtonWithSpinner } from 'components/Button';
import { ShowMoreReviewsButtonText } from 'autogen/translation-keys/trans-website-productreview';
import { useTranslation } from 'react-i18next';
import ReviewTranslationDisclaimerTooltip from 'components/shared/TooltipNotification/ReviewTranslationDisclaimerTooltip';
import { HidePrelaunch, ShowingReviews, SortBy } from 'autogen/translation-keys/trans-website-productreview-list';
import {
    HighestRating,
    LatestReviewed,
    LowestRating,
    MostLiked
} from 'autogen/translation-keys/trans-website-productreview-sort';
import ReviewCard from './ReviewCard/ReviewCard';
import { Container } from '../shared/styles';
import { use_GetAllProductReviewsQuery } from 'autogen/swagger/all/hooks';
import type { ProductReviewListModel } from 'autogen/swagger/all';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const FilterContainer = styled(Container)`
    display: flex;
    flex-direction: column;

    ${breakpoint.up(sizes.md)} {
        flex-direction: row;
        justify-content: space-between;
        margin-bottom: ${Spacing16};
    }
`;

const ReviewContainer = styled(Container)`
    display: flex;
    flex-direction: column;
    position: relative;
`;

const ShowMoreButton = styled(ButtonWithSpinner)`
    margin-bottom: 20px;

    ${breakpoint.up(sizes.md)} {
        width: 320px;
        margin-left: auto;
        margin-right: auto;
    }
`;

const ShowingText = styled.div`
    text-align: center;
    font-weight: bold;
    margin-bottom: 10px;
`;

const HidePrelaunchContainer = styled.div`
    display: flex;
    align-items: center;

    ${breakpoint.up(sizes.md)} {
        order: 1;
        flex-shrink: 0;
        margin-right: ${Spacing16};
    }
`;

const Label = styled.label`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin: 0;
`;

const HidePrelaunchText = styled.span`
    font-size: ${FontSize14};
    margin-left: ${Spacing8};
`;

const SortContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: ${Spacing16};

    ${breakpoint.up(sizes.md)} {
        order: 2;
        flex: 0 1 320px;
        margin-bottom: 0;
        margin-left: auto;
    }
`;

const SortText = styled.span`
    font-weight: bold;

    ${breakpoint.up(sizes.md)} {
        display: none;
    }
`;

const Loading = styled.div`
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3);
    top: 0;
    left: 0;
    z-index: 1;
`;

const ReviewCardList = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${Spacing16};
`;

interface IReviewListProps {
    model: ProductReviewListModel;
}

export const ReviewList = (props: IReviewListProps): JSX.Element => {
    const { model, ...rest } = props;
    const { t } = useTranslation();

    const sortTrans = {
        MostLiked: t(MostLiked),
        LatestReviewed: t(LatestReviewed),
        HighestRating: t(HighestRating),
        LowestRating: t(LowestRating)
    };

    const [hidePrelaunchState, setHidePrelaunchState] = useState(model.hidePrelaunch);
    const [selection, setSelection] = useState({
        value: model.sortBy,
        label: sortTrans[model.sortBy]
    });

    const { data, refetch, isFetching } = use_GetAllProductReviewsQuery(
        { productId: model.productId, hidePrelaunch: hidePrelaunchState, sortBy: selection.value },
        {
            initialData: model,
            refetchOnMount: false,
            retryOnMount: false,
            refetchOnWindowFocus: false,
            keepPreviousData: true
        }
    );

    const {
        productReviews = model.productReviews,
        hasMoreReviews = model.hasMoreReviews,
        totalReviewCount = model.totalReviewCount,
        showPrelaunchFilter = model.showPrelaunchFilter
    } = data ?? {};

    const onSortChange = (option) => {
        setSelection(option);
    };

    const onHidePrelaunchChange = (e) => {
        setHidePrelaunchState(e.target.checked);
    };

    async function onShowMoreClick() {
        refetch();
    }

    const showingText = format(t(ShowingReviews))(productReviews.length, totalReviewCount);

    return (
        <Wrapper {...rest}>
            <FilterContainer>
                <SortContainer>
                    <SortText>{t(SortBy)}</SortText>
                    <Select options={getSortOptions(sortTrans)} value={selection} onChange={onSortChange} />
                </SortContainer>
                {showPrelaunchFilter && (
                    <HidePrelaunchContainer>
                        <Label>
                            <Checkbox checked={hidePrelaunchState} onChange={onHidePrelaunchChange} />
                            <HidePrelaunchText>{t(HidePrelaunch)}</HidePrelaunchText>
                        </Label>
                    </HidePrelaunchContainer>
                )}
            </FilterContainer>

            <ReviewContainer>
                {isFetching && (
                    <Loading>
                        <Loader />
                    </Loading>
                )}
                <ReviewTranslationDisclaimerTooltip model={model.translationTooltip}>
                    <ReviewCardList>
                        {productReviews.map((x) => (
                            <ReviewCard key={x.reviewId} model={x} />
                        ))}
                    </ReviewCardList>
                </ReviewTranslationDisclaimerTooltip>
            </ReviewContainer>
            <ShowingText>{showingText}</ShowingText>
            {hasMoreReviews && (
                <ShowMoreButton onClick={onShowMoreClick} variant="negative">
                    {t(ShowMoreReviewsButtonText)}
                </ShowMoreButton>
            )}
        </Wrapper>
    );
};

export default ReviewList;
